import { useState, useEffect } from 'react';
import { X, ClipboardCheck, CheckCircle2, XCircle, ChevronRight, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { equipmentService } from '../../services/equipment';
import type { ChecklistTemplate, ChecklistTemplateItem } from '../../types/equipment';

interface RunChecklistModalProps {
  equipmentId: number;
  equipmentName: string;
  equipmentType: string;
  sprayApplicationId?: number | null;
  onClose: () => void;
  onCompleted: () => void;
}

type ItemResult = { passed: boolean | null; notes: string };

/**
 * Step 1 — Template picker
 * Step 2 — Run through items
 * Step 3 — Review & submit
 */
function RunChecklistModal({
  equipmentId, equipmentName, equipmentType, sprayApplicationId, onClose, onCompleted,
}: RunChecklistModalProps) {
  const [step, setStep] = useState<'pick' | 'run' | 'review'>('pick');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ChecklistTemplate | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [results, setResults] = useState<Record<number, ItemResult>>({});
  const [overallNotes, setOverallNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Which templates apply to THIS machine — asked of the server rather than
  // filtered here from the org-wide list.
  //
  // Two reasons. The admin list (`/equipment/checklists/templates`) is gated on
  // a create permission, so an operator running their own pre-start got a 403
  // and an empty picker. And the spray pre-start gate decides "has a checklist"
  // with the server's own matching rule; re-implementing it client-side is how
  // a machine ends up blocked by a gate whose remedy screen shows nothing to do.
  useEffect(() => {
    equipmentService.getChecklistTemplates(equipmentId)
      .then(res => {
        if (res.success && res.data) {
          setTemplates(res.data);
          // Auto-select if only one template
          if (res.data.length === 1) {
            loadTemplate(res.data[0]);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingTemplates(false));
  }, [equipmentId]);

  const loadTemplate = async (t: ChecklistTemplate) => {
    setLoadingTemplate(true);
    try {
      const res = await equipmentService.getChecklistTemplate(t.id);
      if (res.success && res.data) {
        const tpl = res.data;
        setSelectedTemplate(tpl);
        // Initialise result map
        const init: Record<number, ItemResult> = {};
        tpl.items.forEach((item: ChecklistTemplateItem) => {
          init[item.id] = { passed: null, notes: '' };
        });
        setResults(init);
        setStep('run');
      }
    } catch { /* template load failed, use defaults */ }
    setLoadingTemplate(false);
  };

  const setItemResult = (itemId: number, passed: boolean) => {
    setResults(r => ({ ...r, [itemId]: { ...r[itemId], passed } }));
  };
  const setItemNotes = (itemId: number, notes: string) => {
    setResults(r => ({ ...r, [itemId]: { ...r[itemId], notes } }));
  };

  const items = selectedTemplate?.items ?? [];
  const allAnswered = items.every(item => results[item.id]?.passed !== null);
  const failedRequired = items.filter(
    item => item.is_required && results[item.id]?.passed === false
  );

  const handleSubmit = async () => {
    if (!selectedTemplate) return;
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        template_id: selectedTemplate.id,
        spray_application_id: sprayApplicationId ?? null,
        notes: overallNotes.trim() || undefined,
        items: items.map(item => ({
          template_item_id: item.id,
          passed: results[item.id]?.passed ?? false,
          notes: results[item.id]?.notes?.trim() || undefined,
        })),
      };
      const res = await equipmentService.recordChecklistCompletion(equipmentId, payload);
      if (res.success) {
        onCompleted();
        onClose();
      } else {
        setError((res as any).error?.message || 'Failed to submit checklist');
      }
    } catch (e: any) {
      setError(e?.message || 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step: Template picker ───────────────────────────────────────────────
  const renderPick = () => (
    <div className="px-6 py-5">
      {loadingTemplates ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-8">
          <ClipboardCheck className="h-10 w-10 text-neutral-300 mx-auto mb-2" />
          <p className="text-neutral-500 text-sm">
            No checklist templates configured for {equipmentType || 'this equipment type'}.
          </p>
          <p className="text-xs text-neutral-400 mt-1">
            Create templates in <a href="/equipment/admin" className="text-primary-600 hover:underline">Equipment Settings</a>.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-neutral-500 mb-3">Select a checklist to run:</p>
          {templates.map(t => (
            <button
              key={t.id}
              onClick={() => loadTemplate(t)}
              disabled={loadingTemplate}
              className="w-full flex items-center justify-between px-4 py-3 bg-white border border-neutral-200 rounded-xl hover:border-primary-400 hover:bg-primary-50 transition-colors text-left disabled:opacity-60"
            >
              <div>
                <p className="font-medium text-neutral-800 text-sm">{t.name}</p>
                {t.description && (
                  <p className="text-xs text-neutral-400 mt-0.5">{t.description}</p>
                )}
                <p className="text-xs text-neutral-400 mt-0.5">
                  {t.items?.length ?? '?'} items
                  {!t.equipment_type && <span className="ml-1.5 text-blue-500">· Applies to all equipment</span>}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-neutral-400 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ── Step: Run checklist ─────────────────────────────────────────────────
  const renderRun = () => (
    <div className="px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
      {items.map((item, idx) => {
        const result = results[item.id];
        const answered = result?.passed !== null;
        const passed = result?.passed === true;
        const failed = result?.passed === false;
        return (
          <div
            key={item.id}
            className={`border rounded-xl p-4 transition-colors ${
              !answered ? 'border-neutral-200 bg-white'
              : passed ? 'border-green-200 bg-green-50'
              : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neutral-100 text-neutral-400 text-xs flex items-center justify-center font-semibold mt-0.5">
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-800">
                  {item.item_text}
                  {item.is_required && (
                    <span className="ml-1.5 text-xs text-neutral-400 font-normal">(required)</span>
                  )}
                </p>

                {/* Pass / Fail buttons */}
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setItemResult(item.id, true)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      passed
                        ? 'bg-green-600 border-green-600 text-white'
                        : 'bg-white border-neutral-200 text-neutral-600 hover:border-green-400 hover:text-green-700'
                    }`}
                  >
                    <CheckCircle2 className="h-4 w-4" /> Pass
                  </button>
                  <button
                    type="button"
                    onClick={() => setItemResult(item.id, false)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      failed
                        ? 'bg-red-600 border-red-600 text-white'
                        : 'bg-white border-neutral-200 text-neutral-600 hover:border-red-400 hover:text-red-700'
                    }`}
                  >
                    <XCircle className="h-4 w-4" /> Fail
                  </button>
                </div>

                {/* Per-item notes — shown when failed */}
                {failed && (
                  <input
                    type="text"
                    value={result.notes}
                    onChange={e => setItemNotes(item.id, e.target.value)}
                    placeholder="Describe the issue…"
                    className="mt-2 w-full border border-red-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-red-300 bg-white"
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ── Step: Review & notes ────────────────────────────────────────────────
  const renderReview = () => {
    const passCount = items.filter(i => results[i.id]?.passed === true).length;
    const failCount = items.filter(i => results[i.id]?.passed === false).length;
    const overallPass = failedRequired.length === 0;

    return (
      <div className="px-6 py-5 space-y-4">
        {/* Summary banner */}
        <div className={`rounded-xl p-4 ${overallPass ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            {overallPass
              ? <CheckCircle2 className="h-5 w-5 text-green-600" />
              : <XCircle className="h-5 w-5 text-red-600" />
            }
            <span className={`font-semibold text-sm ${overallPass ? 'text-green-800' : 'text-red-800'}`}>
              {overallPass ? 'All required items passed' : `${failedRequired.length} required item(s) failed`}
            </span>
          </div>
          <p className="text-xs text-neutral-500">
            {passCount} passed · {failCount} failed · {items.length} total items
          </p>
        </div>

        {/* Failed items list */}
        {failedRequired.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">Failed required items:</p>
            {failedRequired.map(item => (
              <div key={item.id} className="flex items-start gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">{item.item_text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Overall notes */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Overall notes (optional)</label>
          <textarea
            value={overallNotes}
            onChange={e => setOverallNotes(e.target.value)}
            rows={3}
            placeholder="Any additional observations or comments"
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    );
  };

  // ── Footer navigation ───────────────────────────────────────────────────
  const renderFooter = () => {
    if (step === 'pick') {
      return (
        <div className="flex justify-end px-6 pb-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      );
    }
    if (step === 'run') {
      return (
        <div className="flex justify-between px-6 pb-5">
          <Button variant="outline" onClick={() => { setStep('pick'); setSelectedTemplate(null); }}>
            ← Back
          </Button>
          <Button
            variant="primary"
            disabled={!allAnswered}
            onClick={() => setStep('review')}
          >
            Review →
          </Button>
        </div>
      );
    }
    if (step === 'review') {
      return (
        <div className="flex justify-between px-6 pb-5">
          <Button variant="outline" onClick={() => setStep('run')}>← Edit</Button>
          <Button variant="primary" disabled={submitting} onClick={handleSubmit}>
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Checklist'}
          </Button>
        </div>
      );
    }
  };

  const stepTitle = step === 'pick' ? 'Select Checklist' : step === 'run' ? selectedTemplate?.name ?? 'Checklist' : 'Review & Submit';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <ClipboardCheck className="h-5 w-5 text-primary-600" />
            <div>
              <h2 className="font-semibold text-neutral-800 text-sm">{stepTitle}</h2>
              <p className="text-xs text-neutral-500">{equipmentName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step progress */}
        {step !== 'pick' && (
          <div className="flex px-6 py-2 gap-1 flex-shrink-0">
            {(['run', 'review'] as const).map((s, i) => (
              <div key={s} className={`h-1 flex-1 rounded-full ${step === s || (step === 'review' && i === 0) ? 'bg-primary-500' : 'bg-neutral-200'}`} />
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {step === 'pick' && renderPick()}
          {step === 'run' && renderRun()}
          {step === 'review' && renderReview()}
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-100 flex-shrink-0 pt-4">
          {renderFooter()}
        </div>
      </div>
    </div>
  );
}

export default RunChecklistModal;
